'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { exchangeApiV1 } from 'lib/api/exchangeApi';
import { useAuthStore } from 'lib/stores/authStore';

import type { ListMarginWaitlistEntriesResponse, MarginWaitlistStatusResponse } from './marginWaitlist.types';

const USER_WAITLIST_STATUS_STALE_TIME_MS = 5 * 60_000;

export const marginWaitlistKeys = {
    all: ['marginWaitlist'] as const,
    user: (userId: string) => [...marginWaitlistKeys.all, 'user', userId] as const,
    adminEntries: ['marginWaitlist', 'admin', 'entries'] as const,
};

/** Lets the always-mounted visibility modal keep this request idle while closed. */
export function useMarginWaitlistStatusForUser({ enabled = true }: { enabled?: boolean } = {}) {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedIn = useAuthStore((s) => s.isLoggedIn);

    return useQuery({
        queryKey: marginWaitlistKeys.user(userId ?? ''),
        queryFn: async ({ signal }) => {
            const response = await exchangeApiV1.get<MarginWaitlistStatusResponse>(`/users/${userId}/margin_waitlist`, {
                signal,
            });
            return response.data;
        },
        enabled: enabled && !!userId && isLoggedIn,
        staleTime: USER_WAITLIST_STATUS_STALE_TIME_MS,
        // Eligibility (`can_start_onboarding`) flips server-side when onboarding completes. Without a
        // mount refetch, client-side navigation into perps reuses the cached pre-onboarding value for
        // up to staleTime, stranding the user on a closed onboarding flow until a hard refresh. Refetch
        // on mount so entering the perps surface always re-checks eligibility (background refetch — no
        // loading flash, since cached data is served while it revalidates).
        refetchOnMount: 'always',
    });
}

export function useAdminMarginWaitlistEntries() {
    const isLoggedInAdmin = useAuthStore((s) => s.isLoggedInAdmin);
    return useQuery({
        queryKey: marginWaitlistKeys.adminEntries,
        queryFn: async ({ signal }) => {
            const response = await exchangeApiV1.get<ListMarginWaitlistEntriesResponse>(
                '/admin/margin_waitlist/entries',
                {
                    signal,
                },
            );
            return response.data;
        },
        enabled: isLoggedInAdmin,
    });
}

export function useAdminUnlockMarginWaitlistEntry() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (entryId: string) => {
            const response = await exchangeApiV1.post<MarginWaitlistStatusResponse>(
                `/admin/margin_waitlist/entries/${entryId}/unlock`,
                {},
            );
            return response.data;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: marginWaitlistKeys.adminEntries });
        },
    });
}
